import area from '@turf/area';
import distance from '@turf/distance';
import { criarFormatadores } from '@/i18n/formatacao';
const MENSAGENS_PT = {
    coordenadas_invalidas: 'Coordenadas inválidas: use longitude entre -180° e 180° e latitude entre -90° e 90°.',
    minimo_dois_vertices: 'Marque ao menos dois vértices para medir a distância.',
    minimo_tres_vertices: 'Marque ao menos três vértices distintos para medir a área.',
    vertices_repetidos: 'O polígono possui vértices repetidos.',
    auto_intersecao: 'O polígono se cruza: refaça o desenho sem auto-interseção.',
    poligono_degenerado: 'O polígono é degenerado: a área calculada é zero.',
};
function falha(codigo) {
    return { ok: false, codigo, mensagem: MENSAGENS_PT[codigo] };
}
const METROS_POR_QUILOMETRO = 1000;
/** Abaixo disto o polígono é tratado como degenerado (área nula). */
const AREA_MINIMA_M2 = 1e-6;
function coordenadaValida(coordenada) {
    const [longitude, latitude] = coordenada;
    return (Number.isFinite(longitude) &&
        Number.isFinite(latitude) &&
        longitude >= -180 &&
        longitude <= 180 &&
        latitude >= -90 &&
        latitude <= 90);
}
function validarCoordenadas(coordenadas) {
    return coordenadas.every(coordenadaValida);
}
function comprimentoDoSegmentoM(a, b) {
    return distance([...a], [...b], { units: 'kilometers' }) * METROS_POR_QUILOMETRO;
}
function somarSegmentos(coordenadas) {
    let total = 0;
    for (let i = 1; i < coordenadas.length; i += 1) {
        total += comprimentoDoSegmentoM(coordenadas[i - 1], coordenadas[i]);
    }
    return total;
}
/** Distância acumulada (soma dos segmentos consecutivos) de uma linha com ao menos dois vértices. */
export function medirDistancia(coordenadas) {
    if (coordenadas.length < 2) {
        return falha('minimo_dois_vertices');
    }
    if (!validarCoordenadas(coordenadas)) {
        return falha('coordenadas_invalidas');
    }
    return { ok: true, valor: { distanciaM: somarSegmentos(coordenadas) } };
}
function orientacao(a, b, c) {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}
function estaNoIntervalo(a, b, p) {
    return (p[0] >= Math.min(a[0], b[0]) &&
        p[0] <= Math.max(a[0], b[0]) &&
        p[1] >= Math.min(a[1], b[1]) &&
        p[1] <= Math.max(a[1], b[1]));
}
/** Interseção (inclusive toque e sobreposição colinear) entre os segmentos `p1p2` e `p3p4`. */
function segmentosSeCruzam(p1, p2, p3, p4) {
    const d1 = orientacao(p3, p4, p1);
    const d2 = orientacao(p3, p4, p2);
    const d3 = orientacao(p1, p2, p3);
    const d4 = orientacao(p1, p2, p4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
        return true;
    }
    return ((d1 === 0 && estaNoIntervalo(p3, p4, p1)) ||
        (d2 === 0 && estaNoIntervalo(p3, p4, p2)) ||
        (d3 === 0 && estaNoIntervalo(p1, p2, p3)) ||
        (d4 === 0 && estaNoIntervalo(p1, p2, p4)));
}
/**
 * Auto-interseção do anel desenhado (vértices sem repetir o primeiro no fim). Verificação local
 * sobre os lados do anel, no plano longitude/latitude; serve só à geometria desenhada.
 */
function temAutoIntersecao(anel) {
    const n = anel.length;
    const lado = (i) => [
        anel[i],
        anel[(i + 1) % n],
    ];
    for (let i = 0; i < n; i += 1) {
        const [a, b] = lado(i);
        // Lados consecutivos só podem se tocar no vértice comum: sobreposição colinear é inválida.
        const [, c] = lado((i + 1) % n);
        if (orientacao(a, b, c) === 0 && (a[0] - b[0]) * (c[0] - b[0]) + (a[1] - b[1]) * (c[1] - b[1]) > 0) {
            return true;
        }
        for (let j = i + 2; j < n; j += 1) {
            if (i === 0 && j === n - 1) {
                continue;
            }
            const [p, q] = lado(j);
            if (segmentosSeCruzam(a, b, p, q)) {
                return true;
            }
        }
    }
    return false;
}
/** Área e perímetro de um polígono simples com ao menos três vértices distintos (anel aberto). */
export function medirArea(coordenadas) {
    if (!validarCoordenadas(coordenadas)) {
        return falha('coordenadas_invalidas');
    }
    const distintos = new Set(coordenadas.map(([x, y]) => `${x},${y}`));
    if (distintos.size < 3) {
        return falha('minimo_tres_vertices');
    }
    if (distintos.size !== coordenadas.length) {
        return falha('vertices_repetidos');
    }
    if (temAutoIntersecao(coordenadas)) {
        return falha('auto_intersecao');
    }
    const anelFechado = [...coordenadas, coordenadas[0]];
    const areaM2 = area({
        type: 'Polygon',
        coordinates: [anelFechado.map(([x, y]) => [x, y])],
    });
    if (!(areaM2 > AREA_MINIMA_M2)) {
        return falha('poligono_degenerado');
    }
    return { ok: true, valor: { areaM2, perimetroM: somarSegmentos(anelFechado) } };
}
/** Distância ou perímetro: metros abaixo de 1.000 m; quilômetros a partir daí. */
export function formatarComprimento(metros, idioma = 'pt-BR') {
    return criarFormatadores(idioma).comprimento(metros);
}
/** Área: m² abaixo de 10.000 m²; hectares até 1.000.000 m² (exclusive); km² a partir daí. */
export function formatarArea(metrosQuadrados, idioma = 'pt-BR') {
    return criarFormatadores(idioma).area(metrosQuadrados);
}
